import * as React from 'react';
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Chip,
  IconButton,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Eye as EyeIcon } from '@phosphor-icons/react/dist/ssr/Eye';
import { X as XIcon } from '@phosphor-icons/react/dist/ssr/X';

import api from '@src/api/clients/default';
import { useAuth } from '@src/context';
import { DEMO_PREVIEW, clearPageCaches } from '@src/demo/demoMode';

// ─── Context ────────────────────────────────────────────────────────

// sessionStorage, not React state: a refresh mid screen-share used to drop you
// silently back into the staff view. Session-scoped so closing the tab ends the
// preview. The api client reads the same key to send X-View-As-Client, which is
// what makes the server answer as the client on every page.
export const PREVIEW_STORAGE_KEY = 'viewAsClient';

function readStoredPreview() {
  try {
    const raw = window.sessionStorage.getItem(PREVIEW_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && parsed.id ? parsed : null;
  } catch {
    return null;
  }
}

function writeStoredPreview(value) {
  try {
    if (value && value.id) {
      window.sessionStorage.setItem(PREVIEW_STORAGE_KEY, JSON.stringify(value));
    } else {
      window.sessionStorage.removeItem(PREVIEW_STORAGE_KEY);
    }
  } catch {
    /* private mode / storage disabled — the preview just won't survive a reload */
  }
}

const ClientViewContext = React.createContext({
  viewAsClient: null,
  setViewAsClient: () => {},
});

export function useClientView() {
  return React.useContext(ClientViewContext);
}

/**
 * The user the UI should render for: the real one, or a client stand-in while
 * a staff preview is on. Pages read this instead of `useAuth().state.user` so
 * staff-only chrome disappears with the same toggle that scopes the API.
 */
export function useEffectiveUser() {
  const { state } = useAuth();
  const realUser = state?.user;
  const { viewAsClient } = useClientView();

  return React.useMemo(() => {
    const isStaff = Boolean(realUser?.is_staff || realUser?.is_superuser);
    if (!viewAsClient || !isStaff) return realUser;
    return {
      ...realUser,
      is_staff: false,
      is_superuser: false,
      is_client_preview: true,
      client_memberships: [
        {
          client_id: viewAsClient.id,
          client_name: viewAsClient.name,
          client_slug: viewAsClient.slug || viewAsClient.name,
          role: 'admin',
          budget_center_enabled: true,
          ...(viewAsClient.membership || {}),
        },
      ],
    };
  }, [realUser, viewAsClient]);
}

export function ClientViewProvider({ children }) {
  const [viewAsClient, setViewAsClientState] = React.useState(readStoredPreview);

  const setViewAsClient = React.useCallback((value) => {
    clearPageCaches();
    writeStoredPreview(value);
    setViewAsClientState(value || null);
  }, []);

  const value = React.useMemo(
    () => ({ viewAsClient, setViewAsClient }),
    [viewAsClient, setViewAsClient]
  );

  return (
    <ClientViewContext.Provider value={value}>
      {children}
    </ClientViewContext.Provider>
  );
}

// ─── Preview banner ─────────────────────────────────────────────────

/**
 * Full-width bar while a preview is on. The failure mode of a good preview is
 * forgetting you are in one — a small chip in the header is not enough when
 * the screen is being shared.
 */
export function ClientPreviewBanner() {
  const { viewAsClient, setViewAsClient } = useClientView();
  if (!viewAsClient) return null;

  // Demo client is screen-shared with prospects: keep the bar slim and
  // neutral, and don't call it "the client".
  if (viewAsClient.demo) {
    return (
      <Box
        sx={{
          alignItems: 'center',
          bgcolor: 'var(--mui-palette-background-level1)',
          borderBottom: '1px solid var(--mui-palette-divider)',
          display: 'flex',
          gap: 1,
          px: 2,
          py: 0.5,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Demo account — sample data
        </Typography>
        <Button
          color="secondary"
          onClick={() => setViewAsClient(null)}
          size="small"
          sx={{ ml: 'auto', whiteSpace: 'nowrap' }}
        >
          Exit demo
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        alignItems: 'center',
        bgcolor: 'warning.main',
        color: 'warning.contrastText',
        display: 'flex',
        gap: 1,
        px: 2,
        py: 1,
      }}
    >
      <EyeIcon size={18} weight="fill" />
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {`Viewing as ${viewAsClient.name} — this is what the client sees. Changes are disabled.`}
      </Typography>
      <Button
        color="inherit"
        onClick={() => setViewAsClient(null)}
        size="small"
        sx={{ ml: 'auto', whiteSpace: 'nowrap' }}
        variant="outlined"
      >
        Exit preview
      </Button>
    </Box>
  );
}

// ─── Switcher Button (staff only) ───────────────────────────────────

export default function ClientViewSwitcher() {
  const { viewAsClient, setViewAsClient } = useClientView();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [clients, setClients] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const open = Boolean(anchorEl);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    if (!clients.length) loadClients();
  };

  const handleClose = () => setAnchorEl(null);

  const loadClients = async () => {
    setLoading(true);
    try {
      const res = await api.request({
        url: 'dashboard/clients/?fields=id,name,slug&active=true',
        method: 'GET',
      });
      const rows = Array.isArray(res?.data?.results)
        ? res.data.results
        : Array.isArray(res?.data)
          ? res.data
          : [];
      setClients(rows);
    } catch {
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  if (viewAsClient?.demo) {
    return (
      <Chip
        label="Demo"
        size="small"
        variant="outlined"
        onDelete={() => setViewAsClient(null)}
        deleteIcon={<XIcon size={14} />}
      />
    );
  }

  if (viewAsClient) {
    return (
      <Chip
        icon={<EyeIcon size={16} />}
        label={`Viewing as: ${viewAsClient.name}`}
        color="warning"
        size="small"
        onDelete={() => setViewAsClient(null)}
        deleteIcon={<XIcon size={14} />}
        sx={{ fontWeight: 600 }}
      />
    );
  }

  return (
    <>
      <Tooltip title="View as Client">
        <IconButton onClick={handleOpen} size="small">
          <EyeIcon />
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, width: 300 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            View as Client Member
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            See the app as a client member would. Select a client to switch views.
          </Typography>
          <Button
            fullWidth
            onClick={() => {
              setViewAsClient(DEMO_PREVIEW);
              handleClose();
              // Full load so every page starts from demo data, not a half-
              // loaded staff view.
              window.location.assign(`/clients/${DEMO_PREVIEW.slug}/dashboard`);
            }}
            size="small"
            sx={{ mb: 2 }}
            variant="outlined"
          >
            Demo client (sample data)
          </Button>
          <Autocomplete
            options={clients}
            getOptionLabel={(o) => o.name || ''}
            loading={loading}
            onChange={(_, value) => {
              if (value) {
                setViewAsClient(value);
                handleClose();
              }
            }}
            renderInput={(params) => (
              <TextField {...params} label="Select Client" size="small" autoFocus />
            )}
            size="small"
          />
        </Box>
      </Popover>
    </>
  );
}
